import { ArrowUp, ArrowDown, Trash2, Plus, Merge, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SECTION_META, SECTION_ORDER, metaFor } from "@/lib/sections";
import { linesToText, textToLines } from "@/lib/chords";
import { toast } from "sonner";

function newSection() {
  return { id: Math.random().toString(16).slice(2, 10), type: "VERSO", title: "Verso", order: 0, lines: [], annotations: [], notes: "", repeat: 1, confidence: 1 };
}

export default function StructureEditor({ sections, onChange }) {
  const upd = (id, patch) => onChange(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const arr = [...sections];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr.map((s, k) => ({ ...s, order: k })));
  };
  const del = (id) => onChange(sections.filter((s) => s.id !== id).map((s, k) => ({ ...s, order: k })));
  const add = () => onChange([...sections, { ...newSection(), order: sections.length }]);
  const mergeUp = (i) => {
    if (i === 0) return;
    const arr = [...sections];
    arr[i - 1] = { ...arr[i - 1], lines: [...arr[i - 1].lines, ...arr[i].lines], notes: [arr[i - 1].notes, arr[i].notes].filter(Boolean).join("\n") };
    arr.splice(i, 1);
    onChange(arr.map((s, k) => ({ ...s, order: k })));
    toast.success("Seções unidas.");
  };
  const inp = "bg-background border-border rounded-md";

  return (
    <div className="space-y-3" data-testid="structure-editor">
      {sections.map((sec, i) => {
        const meta = metaFor(sec.type);
        const low = (sec.confidence ?? 1) < 0.7;
        return (
          <div key={sec.id} data-testid={`se-section-${sec.id}`} className="rounded-lg border border-border bg-surface p-3 space-y-2" style={{ borderLeftColor: `hsl(var(--${meta.tone}))`, borderLeftWidth: 4 }}>
            <div className="flex items-center gap-1.5 flex-wrap">
              <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={sec.type} onValueChange={(v) => upd(sec.id, { type: v, title: SECTION_META[v]?.label || sec.title, confidence: 1 })}>
                <SelectTrigger data-testid={`se-type-${sec.id}`} className={`${inp} h-8 w-40`}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {SECTION_ORDER.map((t) => <SelectItem key={t} value={t}>{SECTION_META[t].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid={`se-title-${sec.id}`} value={sec.title} onChange={(e) => upd(sec.id, { title: e.target.value })} placeholder="Rótulo" className={`${inp} h-8 w-32`} />
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">×
                <Input data-testid={`se-repeat-${sec.id}`} type="number" min={1} max={8} value={sec.repeat || 1} onChange={(e) => upd(sec.id, { repeat: Math.max(1, parseInt(e.target.value || "1", 10)) })} className={`${inp} h-8 w-14`} />
              </div>
              {low && <span className="text-[10px] text-warning" data-testid={`se-warn-${sec.id}`}>⚠ verifique</span>}
              <div className="ml-auto flex items-center gap-0.5">
                <Button size="icon" variant="ghost" aria-label="Mover para cima" data-testid={`se-up-${sec.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="h-8 w-8"><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" aria-label="Mover para baixo" data-testid={`se-down-${sec.id}`} disabled={i === sections.length - 1} onClick={() => move(i, 1)} className="h-8 w-8"><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" aria-label="Unir com a seção acima" data-testid={`se-merge-${sec.id}`} disabled={i === 0} onClick={() => mergeUp(i)} className="h-8 w-8"><Merge className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" aria-label="Excluir seção" data-testid={`se-del-${sec.id}`} onClick={() => del(sec.id)} className="h-8 w-8 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <Textarea data-testid={`se-content-${sec.id}`} value={linesToText(sec.lines)} onChange={(e) => upd(sec.id, { lines: textToLines(e.target.value) })}
              placeholder="Acordes acima da letra..." className={`${inp} font-mono text-xs min-h-[90px]`} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input data-testid={`se-key-${sec.id}`} value={sec.key || ""} onChange={(e) => upd(sec.id, { key: e.target.value })} placeholder="Tom da seção (medley/modulação)" className={`${inp} h-8 text-xs`} />
              <Input data-testid={`se-song-${sec.id}`} value={sec.song_title || ""} onChange={(e) => upd(sec.id, { song_title: e.target.value })} placeholder="Música (medley)" className={`${inp} h-8 text-xs`} />
              <Input data-testid={`se-songartist-${sec.id}`} value={sec.song_artist || ""} onChange={(e) => upd(sec.id, { song_artist: e.target.value })} placeholder="Artista (medley)" className={`${inp} h-8 text-xs`} />
            </div>
            <Textarea data-testid={`se-notes-${sec.id}`} value={sec.notes || ""} onChange={(e) => upd(sec.id, { notes: e.target.value })}
              placeholder="Observações da banda (ex: baixar dinâmica, entra 2ª voz)..." className={`${inp} text-xs min-h-[40px]`} />
          </div>
        );
      })}
      <Button variant="outline" data-testid="se-add-section" onClick={add} className="rounded-md bg-transparent gap-2 w-full"><Plus className="h-4 w-4" />Adicionar seção</Button>
    </div>
  );
}
